import React, { useCallback, useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  CircularProgress,
  Grid,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import GroupIcon from "@mui/icons-material/Group";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SearchIcon from "@mui/icons-material/Search";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import UserManagementApiService from "../services/UserManagementApiService";
import UsersTable from "../components/user-management/UsersTable";
import CreateUserModal from "../components/user-management/CreateUserModal";
import DeleteConfirmDialog from "../components/user-management/DeleteConfirmDialog";
import { getRoles } from "../utils/auth"; // ✅ IMPORTANT

/* ✅ Stat Card */
const StatCard = ({ icon, label, value }) => (
  <Card
    sx={{
      borderRadius: 3,
      p: 2,
      display: "flex",
      alignItems: "center",
      gap: 2,
      boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
    }}
  >
    <Avatar sx={{ bgcolor: "primary.main", width: 48, height: 48 }}>
      {icon}
    </Avatar>

    <Box>
      <Typography fontWeight={700} fontSize={22}>
        {value}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    </Box>
  </Card>
);

const UserManagement = () => {
  const roles = getRoles();
  const isReadonly = roles.includes("readonly"); // ✅ KEY FLAG

  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMap, setLoadingMap] = useState({});
  const [search, setSearch] = useState("");

  const [createOpen, setCreateOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    status: "",
  });

  const showSnackbar = (message, status) =>
    setSnackbar({ open: true, message, status });

  const setUserLoading = (username, val) =>
    setLoadingMap((prev) => ({ ...prev, [username]: val }));

  const applySearch = useCallback(
    (list, q) =>
      list.filter(
        (u) =>
          u.username.toLowerCase().includes(q.toLowerCase()) ||
          u.email.toLowerCase().includes(q.toLowerCase())
      ),
    []
  );

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await UserManagementApiService.listUsers();
      const list = Array.isArray(res.data) ? res.data : [];
      setUsers(list);
      setFilteredUsers(applySearch(list, search));
    } catch {
      showSnackbar("Failed to load users.", "error");
    } finally {
      setLoading(false);
    }
  }, [search, applySearch]);

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    setFilteredUsers(applySearch(users, search));
  }, [search, users, applySearch]);

  /* ─── Actions ─── */
  const handleCreateUser = async (form) => {
    if (isReadonly) return; // 🚫 block
    setCreateLoading(true);
    try {
      await UserManagementApiService.createUser(form);
      setCreateOpen(false);
      fetchUsers();
    } finally {
      setCreateLoading(false);
    }
  };

  const handleToggleEnable = async (user) => {
    if (isReadonly) return; // 🚫 block
    setUserLoading(user.username, true);
    try {
      user.enabled
        ? await UserManagementApiService.disableUser(user.username)
        : await UserManagementApiService.enableUser(user.username);
      fetchUsers();
    } finally {
      setUserLoading(user.username, false);
    }
  };

  const handleRoleChange = async (user, newRole) => {
    if (isReadonly) return; // 🚫 block
    if (user.role === newRole) return;
    setUserLoading(user.username, true);
    try {
      await UserManagementApiService.updateRole({
        username: user.username,
        oldRole: user.role,
        newRole,
      });
      fetchUsers();
    } finally {
      setUserLoading(user.username, false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (isReadonly) return; // 🚫 block
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await UserManagementApiService.deleteUser(deleteTarget.username);
      setDeleteTarget(null);
      fetchUsers();
    } finally {
      setDeleteLoading(false);
    }
  };

  /* ─── Stats ─── */
  const totalUsers = users.length;
  const adminCount = users.filter((u) => u.role === "admin").length;
  const readonlyCount = users.filter((u) => u.role === "readonly").length;
  const disabledCount = users.filter((u) => !u.enabled).length;

  return (
    <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Avatar sx={{ bgcolor: "primary.main" }}>
            <GroupIcon />
          </Avatar>

          <Box>
            <Typography variant="h5" fontWeight={700}>
              User Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage users and roles
            </Typography>
          </Box>
        </Box>

        {/* ✅ DISABLED FOR READONLY */}
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
          disabled={isReadonly}
        >
          Create User
        </Button>
      </Box>

      {/* GRID */}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard icon={<GroupIcon />} label="Total Users" value={totalUsers} />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard icon={<AdminPanelSettingsIcon />} label="Admins" value={adminCount} />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard icon={<VisibilityIcon />} label="Readonly" value={readonlyCount} />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard icon={<PersonOffIcon />} label="Disabled" value={disabledCount} />
        </Grid>
      </Grid>

      {/* Main */}
      <Card sx={{ p: 2, borderRadius: 3 }}>
        <TextField
          size="small"
          placeholder="Search..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 2, width: 320 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        {loading ? (
          <Box sx={{ textAlign: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <UsersTable
            users={filteredUsers}
            loadingMap={loadingMap}
            onToggleEnable={handleToggleEnable}
            onRoleChange={handleRoleChange}
            onDeleteClick={(u) =>
              !isReadonly && setDeleteTarget(u) // 🚫 block delete click
            }
            isReadonly={isReadonly} // ✅ PASS DOWN
          />
        )}
      </Card>

      <CreateUserModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={handleCreateUser}
        loading={createLoading}
      />

      <DeleteConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        username={deleteTarget?.username || ""}
        loading={deleteLoading}
      />
    </Box>
  );
};

export default UserManagement;